/* eslint-disable prefer-destructuring */
import * as Highcharts from 'highcharts';
import {
  NWC_CHART_TITLE,
  QOFE_BRIDGE_CHART_TITLE,
} from '../engine/utilities/constants';
import { screenReaderPointDescriptorFactory } from './types/createScreenReaderPointDescription';

export const pxToRem = (px) => {
  const remVal = px / 16;
  return `${remVal}rem`;
};

const makeDefaultChartOptions = (decimalCount) => {
  const pointDescriptorFn = screenReaderPointDescriptorFactory(decimalCount);

  return {
    chart: {
      backgroundColor: Highcharts.getOptions().colors[6],
      height: 300,
      styledMode: false,
      borderRadius: 2,
      spacing: [24, 24, 24, 24],
    },

    accessibility: {
      announceNewData: {
        enabled: true,
        interruptUser: true,
        minAnnounceInterval: 4000,
      },
      point: {
        descriptionFormatter: pointDescriptorFn,
      },
    },

    caption: {
      text: '',
    },

    title: {
      text: '',
      align: 'left',
      useHTML: true,
      style: {
        color: Highcharts.getOptions().colors[4],
        fontSize: pxToRem(22),
        fontFamily: 'Helvetica',
        fontWeight: 'bold',
      },
    },

    subtitle: {
      text: '',
      align: 'left',
      style: {
        color: Highcharts.getOptions().colors[5],
      },
    },

    credits: {
      enabled: false,
    },

    xAxis: {
      title: { text: '' },
      labels: {
        style: {
          color: Highcharts.getOptions().colors[7],
          fontSize: pxToRem(16),
        },
      },
    },

    yAxis: {
      visible: false,
    },

    legend: {
      enabled: false,
    },

    tooltip: {
      enabled: false,
    },

    series: [{ data: [] }],
  };
};

const makeDefaultQofEBridgeOptions = (decimalCount) => {
  const options = makeDefaultChartOptions(decimalCount);

  options.title.text = QOFE_BRIDGE_CHART_TITLE;

  options.chart.type = 'waterfall';
  options.chart.height = 380;
  options.chart.marginTop = 80;
  options.chart.spacing = [0, 0, 0, 0];

  options.xAxis.min = 0.5;
  options.xAxis.type = 'category';
  options.xAxis.labels.align = 'left';
  options.xAxis.labels.style.color = Highcharts.getOptions().colors[4];
  options.xAxis.lineColor = Highcharts.getOptions().colors[8];
  options.xAxis.style = { color: Highcharts.getOptions().colors[4] };

  options.series = [
    {
      upColor: Highcharts.getOptions().colors[2],
      color: Highcharts.getOptions().colors[3],
      data: [],
      borderWidth: 0,
      lineColor: Highcharts.getOptions().colors[4],
      pointPadding: 0,
    },
  ];

  options.responsive = {
    rules: [
      {
        condition: {
          maxWidth: 500,
        },
        chartOptions: {
          xAxis: {
            labels: {
              align: 'right',
              x: 25,
              rotation: -60,
              overflow: 'allow',
            },
          },
        },
      },
      {
        condition: {
          maxWidth: 300,
        },
        chartOptions: {
          chart: {
            marginTop: 85,
          },
        },
      },
    ],
  };

  options.plotOptions = {
    waterfall: {
      pointPlacement: 0.25,
    },
  };

  return options;
};

const makeDefaultNwcChartOptions = (decimalCount) => {
  const options = makeDefaultChartOptions(decimalCount);

  options.title.text = NWC_CHART_TITLE;
  options.title.margin = 30;

  options.chart.type = 'line';

  options.legend = {
    enabled: true,
    itemStyle: {
      color: Highcharts.getOptions().colors[4],
    },
  };

  options.plotOptions = {
    line: {
      marker: { enabled: false },
    },
  };

  options.xAxis = {
    labels: {
      ...options.xAxis,
      y: 24,
      style: {
        color: Highcharts.getOptions().colors[7],
        fontSize: pxToRem(12),
      },
    },
    lineWidth: 0,
  };

  options.yAxis = {
    title: { text: '' },
    labels: {
      format: '{value}',
      style: {
        color: Highcharts.getOptions().colors[7],
        fontSize: pxToRem(12),
      },
    },
    tickAmount: 5,
    gridLineColor: Highcharts.getOptions().colors[8],
  };

  return options;
};

export { makeDefaultQofEBridgeOptions, makeDefaultNwcChartOptions };
